"use client";

import * as React from "react";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "../ui/sidebar";
import { ChatHeader } from "./chat-header";
import { ChatSidebar, type ChatSidebarProps } from "./chat-sidebar";

export interface ChatAppShellProps extends ChatSidebarProps {
  /** Conversation title in the single main-area top bar. */
  title?: string;
  /** Optional brand node in the top bar; nothing is rendered when absent. */
  brand?: React.ReactNode;
  /** The chat surface — rendered inside shadcn's SidebarInset, below the top bar. */
  children: React.ReactNode;
}

/**
 * The chat application shell: sidebar + the single main-area top bar + the chat surface.
 *
 * The shell owns viewport and overflow, because neither primitive bounds a height — SidebarProvider's
 * wrapper is `min-h-svh` (a *min*, so it grows with content) and SidebarInset carries none. Three
 * composition-level classes make the consumer's `flex-1 overflow-y-auto` scroller actually scroll:
 * `h-dvh overflow-hidden` on the provider so the page never grows, `shrink-0` on the top bar so a long
 * transcript cannot compress it, and `flex min-h-0 flex-1 flex-col` around `children` so the scroller
 * can shrink below its content. ui/sidebar.tsx is not modified.
 *
 * Must be rendered inside a ThemeProvider (the footer's ThemeToggle calls useTheme). It provides its
 * own SidebarProvider; apps own the ThemeProvider.
 */
export function ChatAppShell({
  title,
  brand,
  children,
  ...sidebarProps
}: ChatAppShellProps) {
  return (
    <SidebarProvider className="h-dvh overflow-hidden">
      <ChatSidebar {...sidebarProps} />
      {/* min-w-0: SidebarInset is a flex row item, so its min-width defaults to auto — its
          content-based minimum. Measured against the compiled stylesheet at a 900px viewport beside
          the 256px expanded sidebar (644px available): a 100-character conversation title drives the
          pane to 896px, and so does a long unbroken token in a message. The title span's own
          `min-w-0 truncate` shrinks the span inside its header but does not remove this ancestor's
          automatic minimum. With `overflow-hidden` on the provider there is no horizontal scroll
          path, so the excess is simply clipped off-screen. */}
      <SidebarInset className="min-w-0">
        <ChatHeader
          title={title}
          brand={brand}
          trigger={<SidebarTrigger />}
          className="shrink-0"
        />
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </SidebarInset>
    </SidebarProvider>
  );
}
